//#region Angular Imports

import { StateService } from "@uirouter/angularjs";

//#endregion

import { Api, LocalDictionaryFactory } from "../api/api.module"

import { Enums, Activity } from "../../models/common"
import { OpConsumerType } from "../../models/core/op-consumer-type"

import { IUserSession, CommonHelper, EventsNames, ToastHelper, LocalizationHelper, TemplateHelper, ConfigHelper } from "../../helpers"
import { ConsumerTypeHelper } from "./consumer-types.helper"

import { ButtonsOptions, GridTypes, GridController } from "../common/grid/grid.controller"
import { BaseGridController, ScopeBaseGrid } from "../common/common.base.grid.controller"
import { SystemService } from "../../services/system"

export interface ConsumerTypeScope extends ScopeBaseGrid {
    deleteConfirmationOptions: {
        title: string

        onConfirmed: () => void
    }
}

export class ConsumerTypeController extends BaseGridController {

    // #region Consts

    private static get GET_CONSUMER_TYPE_GRID_NAME(): string { return 'ConsumerTypesGrid' }

    // #endregion


    // #region Members

    private grid: GridController<OpConsumerType>

    // #endregion


    // #region Ctor

    constructor(
        protected $scope: ConsumerTypeScope,
        $state: StateService,
        $timeout: ng.ITimeoutService,
        api: Api,
        localDictionary: LocalDictionaryFactory,
        protected userSession: IUserSession,
        protected systemService: SystemService,
        referenceType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super($scope, $timeout, $state, userSession, systemService, api, localDictionary, null, referenceType)

        this.initializeEvents()
        this.setMainGridOptions()
        this.setConfirmationsOptions()
    }

    // #endregion

    // #region Events

    private onInitialized(e: any, grid: GridController<OpConsumerType>): void {
        this.dataGrid = e.component
        this.grid = grid

        this.setGridId(e.tableId)
    }

    private onAdd(): void {
        this.$scope.$broadcast(EventsNames.INITIALIZE_CREATE_EDIT_POPUP, ConsumerTypeHelper.getCreateEditOptions(this.userSession))
    }

    private onEdit(): void {
        if (this.grid.selectedCount == 1) {
            this.$scope.$broadcast(EventsNames.INITIALIZE_CREATE_EDIT_POPUP, ConsumerTypeHelper.getCreateEditOptions(this.userSession, this.grid.selectedItemId, this.grid.selectedItem))
        } else {
            ToastHelper.showInfo(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.BulkEditIsUnsupported))
        }
    }

    private onDelete(): void {
        this.$scope.$broadcast(
            EventsNames.OPEN_CONFIRMATION_POPUP + this.$scope.popupId,
            LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName)
        )
    }

    private onDeleted(): void {
        this.grid.dxGrid.refresh()
    }

    private onConfirmDelete(): void {
        ConsumerTypeHelper
            .deleteConsumerType(this.userSession, this.grid.selectedItem)
            .then(() => {
                this.onDeleted()
            })
    }

    private onShowDetails(e: any): void {
        this.showDetails(e, ConsumerTypeHelper.getDetailsOptions(this.grid.selectedItemId, this.grid.pageItemsIds))
    }

    private setConfirmationsOptions(): void {
        this.$scope.deleteConfirmationOptions = {
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),

            onConfirmed: () => this.onConfirmDelete()
        }
    }

    // #endregion

    //#region Events

    private initializeEvents(): void {        
        this.$scope.$on(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName), (event: ng.IAngularEvent, ...args: any[]) => {
            this.onDeleted()
        })

        this.$scope.$on(EventsNames.CLOSE_CREATE_EDIT_POPUP, (event: ng.IAngularEvent, ...args: any[]) => this.dataGrid.refresh())
    }

    //#endregion

    // #region Setup
    private getContextMenuItems(isTouchScreen: boolean = false): any[] {
        const items: any = []

        if (this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_TYPE_EDIT)) {
            items.push({
                beginGroup: true,
                template: TemplateHelper.getContextMenuItem(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Edit), ConfigHelper.Icon.EDIT, this.userSession),

                onItemClick: (e: any) => this.onEdit(),
            })
        }

        return this.mergeWithDefaultItems(isTouchScreen, items)
    }

    private getGridButtonsOptions(): ButtonsOptions {
        return {
            enabled: true,

            addPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_TYPE_ADD) ? "['" + Activity[Activity.CONSUMER_TYPE_ADD] + "']" : null,
            deletePermissions: this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_TYPE_DEL) ? "['" + Activity[Activity.CONSUMER_TYPE_DEL] + "']" : null,
            editPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_TYPE_EDIT) ? "['" + Activity[Activity.CONSUMER_TYPE_EDIT] + "']" : null,
            showDetailsPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_TYPE_LIST) ? "['" + Activity[Activity.CONSUMER_TYPE_LIST] + "']" : null,

            onAddClick: () => this.onAdd(),
            onEditClick: () => this.onEdit(),
            onDeleteClick: () => this.onDelete(),
            onShowClick: (e: any) => this.onShowDetails(e),
        }
    }

    private setMainGridOptions(): void {
        this.$scope.mainGridOptions = {
            addCustomFilters: true,
            buttonsOptions: this.getGridButtonsOptions(),
            columnsDictionary: ConsumerTypeHelper.getMainGridColumns(),
            idReferenceType: this.referenceType,
            localStorageVarForSavedEntity: CommonHelper.localStorageVarForSavedEntity.consumerTypeSaved,
            name: ConsumerTypeController.GET_CONSUMER_TYPE_GRID_NAME,
            preExecuteClearFilter: this.preExecuteGridClearFilter,
            referenceId: this.referenceType,
            type: GridTypes.MAIN,

            allowColumnReordering: !this.userSession
                .getLoggedOperator()
                .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.ConsumerTypeModuleMainGrid),
            getDataSource: () => this.userSession
                .getDataProvider().ConsumerType
                .getConsumerTypeFilter([], 0, 0, null)
                .then((result: any) => result.objects),

            contextMenuItems: (isTouchScreen: boolean = false) => this.getContextMenuItems(isTouchScreen),
            onInitialized: (e: any, grid: GridController<OpConsumerType>) => this.onInitialized(e, grid),
            onDataReady: (data: any) => this.onDataReady(data),
            onRowDoubleClick: (e: any) => this.onShowDetails(e)
        }
    }

    // #endregion
}